import {
  CustomerAccount,
  InvestmentDetails,
  InvestmentPlan,
  PortfolioAllocation,
  PortfolioAllocationV2,
  StrategyInfo,
  StrategyStock,
  StrategyStockAllocationEve2
} from '@inyova/models';

import { createAction, props } from '@ngrx/store';

import {
  CompanyDetailData,
  CompanyListItem,
  CriteriumData,
  CustomerInvestmentData,
  Notification,
  RiskAnswer,
  RiskSurveyQuestion,
  SearchCompany,
  StrategyStockAllocation,
  TopicData
} from '@shared/models/Strategy';

export const getLatestStrategyData = createAction('[Strategy] Get latest strategy data', props<{ fetchAll: boolean }>());

export const getLatestStrategyDataEve2 = createAction('[Strategy] Get latest strategy data for eve 2');

export const fetchStrategyEditMode = createAction('[Strategy] Fetch strategy edit mode');
export const fetchStrategyEditModeSuccess = createAction('[Strategy] Fetch strategy edit mode success', props<{ changeStrategyDisabled: boolean }>());
export const fetchStrategyEditModeFail = createAction('[Strategy] Fetch strategy edit mode fail');

export const updateStrategyEditMode = createAction('[Strategy] Update strategy edit mode', props<{ editMode: boolean }>());
export const updateStrategyEditModeSuccess = createAction(
  '[Strategy] Update strategy edit mode success',
  props<{ changeStrategyDisabled: boolean }>()
);
export const updateStrategyEditModeFail = createAction('[Strategy] Update strategy edit mode fail');

export const discardStrategyChanges = createAction('[Strategy] Discard strategy changes');
export const discardStrategyChangesSuccess = createAction('[Strategy] Discard strategy changes success');
export const discardStrategyChangesFail = createAction('[Strategy] Discard strategy changes fail');

export const saveStrategyChanges = createAction('[Strategy] Save strategy changes');
export const saveStrategyChangesSuccess = createAction('[Strategy] Save strategy changes success');
export const saveStrategyChangesFail = createAction('[Strategy] Save strategy changes fail');

export const getTopicsData = createAction('[Strategy] Get topics data action');
export const getTopicsDataSuccess = createAction('[Strategy] Get topics data success', props<{ customer: TopicData[]; topics: TopicData[] }>());
export const getTopicsDataFail = createAction('[Strategy] Get topics data fail');

export const sendCompanySuggestion = createAction('[Strategy] Send company suggestion', props<{ message: string }>());
export const sendCompanySuggestionSuccess = createAction('[Strategy] Send company suggestion success');
export const sendCompanySuggestionFail = createAction('[Strategy] Send company suggestion fail');

export const generateStrategy = createAction('[Strategy] Generate strategy');
export const generateStrategySuccess = createAction(
  '[Strategy] Generate strategy success',
  props<{ customerAccountDetail: CustomerAccount; fetchAll: boolean }>()
);
export const generateStrategyFail = createAction('[Strategy] Generate strategy fail');

export const getCriteriaData = createAction('[Strategy] Get criteria data action');
export const getCriteriaDataSuccess = createAction(
  '[Strategy] Get criteria data success',
  props<{ customer: CriteriumData[]; criteria: CriteriumData[] }>()
);
export const getCriteriaDataFail = createAction('[Strategy] Get criteria data fail');

export const setCompanyDetailLoading = createAction('[Strategy] Set company loading');

export const setExpandedCompanyId = createAction('[Strategy] Set expanded company ID', props<{ companyId: number }>());

export const getCompanyDetail = createAction('[Strategy] Get company detail data', props<{ companyId: number }>());
export const getCompanyDetailSuccess = createAction('[Strategy] Get company detail data success', props<CompanyDetailData>());
export const getCompanyDetailFail = createAction('[Strategy] Get company detail data fail');
export const emptyCompanyDetail = createAction('[Strategy] Empty company detail data');

export const getCompanyList = createAction('[Strategy] Get company list data');
export const getCompanyListSuccess = createAction(
  '[Strategy] Get company list data success',
  props<{ blacklist: CompanyListItem[]; wishlist: CompanyListItem[]; yova_stocks: CompanyListItem[] }>()
);
export const getCompanyListFail = createAction('[Strategy] Get company list data fail');

export const getPortfolioCompanyList = createAction('[Strategy] Get portfolio company list data');
export const getPortfolioCompanyListSuccess = createAction(
  '[Strategy] Get portfolio company list data success',
  props<{
    blacklist: CompanyListItem[];
    wishlist: CompanyListItem[];
    yova_stocks: CompanyListItem[];
    female_leadership: number;
    broken_pf: boolean;
  }>()
);
export const getPortfolioCompanyListFail = createAction('[Strategy] Get portfolio company list data fail');

export const hideBrokenPortfolioWarning = createAction('[Strategy] Hide broken portfolio warning');
export const hideBrokenPortfolioUndoMessage = createAction('[Strategy] Hide broken portfolio undo message');

export const updatePortfolioCompanyList = createAction(
  '[Strategy] Update portfolio company list data',
  props<{ wishedCompanyIDs: number[]; blockedCompanyIDs: number[] }>()
);
export const updatePortfolioCompanyListFail = createAction('[Strategy] Update portfolio company list data fail');

export const setTabIndex = createAction('[Strategy] Set tab index', props<{ tabIndex: number }>());
export const setGuaranteeVisibility = createAction('[Strategy] Set guaraantee visibility', props<{ value: boolean }>());

export const getCustomerInvestment = createAction('[Strategy] Get customer investment');
export const getCustomerInvestmentSuccess = createAction(
  '[Strategy] Get customer investment success',
  props<{ investmentData: CustomerInvestmentData }>()
);
export const getCustomerInvestmentFail = createAction('[Strategy] Get customer investment fail');

export const getWithdrawalData = createAction('[Strategy] Get withdrawal data');
export const getWithdrawalDataSuccess = createAction(
  '[Strategy] Get withdrawal data success',
  props<{ data: PortfolioAllocation | PortfolioAllocationV2; accountReportHasData: boolean; hasFeatureFlag: boolean }>()
);
export const getWithdrawalDataFail = createAction('[Strategy] Get withdrawal data fail');

export const setNotification = createAction('[Strategy] Set strategy notification', props<{ notification: Notification }>());
export const setNotificationShowStatus = createAction('[Strategy] Set strategy notification show status', props<{ show: boolean }>());

export const setStrategyWasChanged = createAction(
  '[Strategy] Set strategy was changed',
  props<{ modalOpened: boolean; redirectUrl: string; strategyWasChanged: boolean }>()
);

export const setStrategyWasChangedEve2 = createAction('[Strategy] Set strategy was changed', props<{ strategyWasChanged: boolean }>());

export const undoStrategyActions = createAction('[Strategy] Undo strategy actions data', (source: 'dialog' | 'notification' = 'notification') => ({
  source
}));
export const undoStrategyActionsFail = createAction('[Strategy] Undo strategy actions data fail');

export const getCustomerDiscardedWishes = createAction('[Strategy] Get customer discarded wishes');
export const getCustomerDiscardedWishesSuccess = createAction(
  '[Strategy] Get customer discarded wishes success',
  props<{ discardedWishes: string[] }>()
);
export const getCustomerDiscardedWishesFail = createAction('[Strategy] Get customer discarded wishes fail');

export const setDiscardedWishesDialog = createAction(
  '[Strategy] Set discarded wishes dialog',
  props<{ modalOpened: boolean; redirectUrl: string; discardedWishesDialog: boolean }>()
);

export const getRiskQuestions = createAction('[Strategy] Get risk questions');
export const setCHRiskQuestions = createAction('[Strategy] Set CH risk questions', props<{ riskSurveyQuestions: RiskSurveyQuestion[] }>());
export const set3ARiskQuestions = createAction('[Strategy] Set 3A risk questions', props<{ riskSurveyQuestions: RiskSurveyQuestion[] }>());
export const getRiskQuestionsFail = createAction('[Strategy] Get risk questions fail');

export const getRiskStockSplit = createAction('[Strategy] Get stock split', props<{ riskAnswers: RiskAnswer[] }>());
export const getRiskStockSplitSuccess = createAction(
  '[Strategy] Get stock split success',
  props<{ riskStockSplit: number; maxStockSplit: number }>()
);
export const getRiskStockSplitFail = createAction('[Strategy] Get stock split fail');

// Strategy
// Update topics
export const updateTopics = createAction(
  '[Strategy] Update topics',
  props<{ continueToPortfolio: boolean; updateObject: { topics: number[]; criteria: number[] } }>()
);
export const updateTopicsSuccess = createAction('[Strategy] Update topics success', props<{ continueToPortfolio: boolean; nextStep: number }>());
// Update investment
export const updateInvestment = createAction(
  '[Strategy] Update investment',
  props<{ continueToPortfolio: boolean; updateObject: { one_time_investment: number; period_investment: number } }>()
);
export const updateInvestmentSuccess = createAction(
  '[Strategy] Update investment success',
  props<{ continueToPortfolio: boolean; nextStep: number }>()
);
// Update risk
export const updateRisk = createAction(
  '[Strategy] Update risk',
  props<{
    continueToPortfolio: boolean;
    updateObject: { one_time_investment: number; period_investment: number; risk_answers: RiskAnswer[] };
  }>()
);
export const updateRiskSuccess = createAction('[Strategy] Update risk success', props<{ continueToPortfolio: boolean; nextStep: number }>());

export const updateRiskEve2 = createAction(
  '[Strategy] Update risk Eve2',
  props<{
    risk_answers: RiskAnswer[];
    selected_stock_split: number;
  }>()
);
export const updateRiskSuccessEve2 = createAction('[Strategy] Update risk Eve2 success');
export const updateRiskFailedEve2 = createAction('[Strategy] Update risk Eve2 failed');

export const updateRiskRecheckEve2 = createAction(
  '[Strategy] Update risk recheck Eve2',
  props<{
    risk_answers: RiskAnswer[];
    selected_stock_split: number;
  }>()
);
export const updateRiskRecheckSuccessEve2 = createAction('[Strategy] Update risk recheck Eve2 success');
export const updateRiskRecheckFailedEve2 = createAction('[Strategy] Update risk recheck Eve2 failed');

export const discardRiskRecheckEve2 = createAction('[Strategy] Discard risk recheck Eve2');

export const showRiskToStrategyLoaderEve2 = createAction('[Strategy] Show Risk to Strategy Loader Eve2');
export const hideRiskToStrategyLoaderEve2 = createAction('[Strategy] Hide Risk to Strategy Loader Eve2');
export const navigateFromRiskPageToStrategyReview = createAction(
  '[Strategy] Navigate from Risk page to Strategy Review',
  props<{
    risk_answers: RiskAnswer[];
    selected_stock_split: number;
  }>()
);
export const saveRiskFormDataEve2 = createAction(
  '[Strategy] Save risk profile payload',
  props<{
    risk_answers: RiskAnswer[];
    selected_stock_split: number;
  }>()
);
export const enterStrategyEditModeEve2 = createAction('[Strategy] Enter Strategy edit mode Eve2');
export const discardStrategyChangesEve2 = createAction('[Strategy] Discard strategy changes Eve2');
export const saveStrategyChangesEve2 = createAction('[Strategy] Save strategy changes Eve2');
export const saveStrategyChangesSuccessEve2 = createAction('[Strategy] Save strategy changes success Eve2');
export const saveStrategyChangesFailEve2 = createAction('[Strategy] Save strategy changes fail Eve2');
export const updateAndNavigateToURL = createAction('[Strategy] Update and navigate to URL', props<{ url: string }>());

// send investment
export const updateInvestmentPlan = createAction(
  '[Strategy] Update investment plan',
  props<{
    initial_investment: number;
    monthly_investment: number;
  }>()
);
export const updateInvestmentPlanSuccess = createAction(
  '[Strategy] Update investment plan success',
  props<{ nextStep: number; investmentPlan: InvestmentPlan }>()
);

export const getInvestmentPlan = createAction('[Strategy] Get investment plan');
export const getInvestmentPlanSuccess = createAction('[Strategy] Get investment plan success', props<InvestmentPlan>());
export const getInvestmentPlanFail = createAction('[Strategy] Getting investment plan failed');

export const getStrategyInfo = createAction('[Strategy] Get strategy info');
export const getStrategyInfoSuccess = createAction('[Strategy] Get strategy info success', props<StrategyInfo>());
export const getStrategyInfoFail = createAction('[Strategy] Get strategy info fail');

export const getFundBreakdown = createAction('[Strategy] Get fund breakdown');
export const getFundBreakdownSuccess = createAction('[Strategy] Get fund breakdown success', props<{ stocks: StrategyStock[] }>());
export const getFundBreakdownFail = createAction('[Strategy] Get fund breakdown fail');

export const getInvestmentDetails = createAction('[Strategy] Get investment details');
export const getInvestmentDetailsSuccess = createAction('[Strategy] Get investment details success', props<InvestmentDetails>());
export const getInvestmentDetailsFail = createAction('[Strategy] Get investment details fail');

export const updateRiskRecheck = createAction('[Strategy] Update risk recheck', props<{ risk_answers: RiskAnswer[] }>());

export const updateFailed = createAction('[Strategy] Update failed');

export const fetchSearchCompanies = createAction('[Strategy] Fetch all companies that match search', props<{ searchedTerm: string }>());
export const fetchSearchCompaniesSuccess = createAction(
  '[Strategy] Fetch all companies that match search success',
  props<{ searchCompanies: SearchCompany[] }>()
);
export const fetchSearchCompaniesFail = createAction('[Strategy] Fetch all companies that match search fail');

export const fetchStockAllocations = createAction('[Strategy] Fetch stock allocations');
export const fetchStockAllocationsSuccess = createAction(
  '[Strategy] Fetch stock allocations success',
  props<{ stockAllocation: StrategyStockAllocation }>()
);
export const fetchStockAllocationsFail = createAction('[Strategy] Fetch stock allocations fail');

export const fetchStockAllocationEve2 = createAction('[Strategy] Fetch stock allocations eve2');
export const fetchStockAllocationEve2Success = createAction(
  '[Strategy] Fetch stock allocations eve2 success',
  props<{ stockAllocation: StrategyStockAllocationEve2 }>()
);
export const fetchStockAllocationEve2Fail = createAction('[Strategy] Fetch stock allocations eve2 fail');

export const setStockAllocationDetails = createAction(
  '[Strategy] Set stock allocation details',
  props<{ currentTab: StrategyStockAllocation['currentTab']; selectedIndex: number }>()
);

export const disableInterviewNotification = createAction('[Strategy] Disable interview notification', props<{ showWidget: boolean }>());
export const disableInterviewNotificationSuccess = createAction(
  '[Strategy] Disable interview notification success',
  props<{ showWidget: boolean }>()
);
export const disableInterviewNotificationFailed = createAction('[Strategy] Disable interview notification failed');

export const updateToAlmostProspect = createAction('[Strategy] Update account to Almost Prospect step status');
export const updateToAlmostProspectSuccess = createAction('[Strategy] Update account to Almost Prospect step status success');
export const updateToAlmostProspectFail = createAction('[Strategy] Update account to Almost Prospect step status fail');

export const skipSaxoOnboarding = createAction('[Strategy] Skip Saxo onboarding');
export const skipSaxoOnboardingFail = createAction('[Strategy] Skip Saxo onboarding fail');

export const closeGeneratePortfolioLoader = createAction('[Strategy] Close generate portfolio loader');

export const updateQLOnboardingStep = createAction('[Strategy] Update QL onboarding step', props<{ step: number }>());
export const updateQLOnboardingStepSuccess = createAction('[Strategy] Update QL onboarding step success', props<{ step: number }>());
export const updateQLOnboardingStepFail = createAction('[Strategy] Update QL onboarding step fail');
